/**
 * V2 Layout
 * 
 * Root layout for the v2 redesigned UI.
 * Uses AppShell for navigation that adapts to breakpoints.
 * 
 * @see docs/design/01_MOBILE_SPEC.md
 * @see docs/design/02_TABLET_SPEC.md
 * @see docs/design/03_DESKTOP_SPEC.md
 */

import React, { useEffect, useState, useCallback } from 'react';
import { View, ActivityIndicator } from 'react-native';
import { Slot, usePathname, Redirect } from 'expo-router';
import { useAuthStore } from '../../store/authStore';
import { useStyles } from '../../hooks/useStyles';
import { AppShell } from '../../components/v2/navigation';
import { logger } from '../../shared/utils/logger';
import { useActivityFeed } from '../../hooks/useActivityFeed';

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Extract nav ID from pathname
 */
function getNavIdFromPath(pathname: string): string {
  // /v2/home -> home
  // /v2/portfolio -> portfolio
  // /v2/portfolio/123 -> portfolio
  const segments = pathname.split('/').filter(Boolean);
  if (segments[0] === 'v2' && segments[1]) {
    return segments[1];
  }
  return 'home';
}

// =============================================================================
// COMPONENT
// =============================================================================

export default function V2Layout() {
  const pathname = usePathname();
  const { tokens } = useStyles();
  
  // Auth state
  const initialized = useAuthStore((state) => state.initialized);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated());
  const user = useAuthStore((state) => state.user);
  
  // Activity badge from real feed
  const { unreadCount: activityBadgeCount } = useActivityFeed();
  
  // Determine active nav from pathname
  const activeNavId = getNavIdFromPath(pathname);
  const isAdmin = user?.role === 'admin';

  // Log navigation for debugging
  useEffect(() => {
    logger.debug('V2 navigation', 'navigation', { pathname, activeNavId });
  }, [pathname, activeNavId]);

  // Loading state
  if (!initialized) {
    return (
      <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: tokens.color.background }}>
        <ActivityIndicator size="large" color={tokens.color.primary} />
      </View>
    );
  }

  // Auth redirect
  if (!isAuthenticated) {
    return <Redirect href="/(auth)/login" />;
  }

  return (
    <AppShell
      activeNavId={activeNavId}
      activityBadgeCount={activityBadgeCount}
    >
      <Slot />
    </AppShell>
  );
}

